import { Injectable } from '@angular/core';
import type { IChatMessageResult, IGuid } from '@core/types/chat.types';
import { TranslateService } from "@ngx-translate/core";
import { BehaviorSubject } from "rxjs";

export const CHAT_ERROR_NAME = {
    SESSION_NOT_FOUND: 'CHAT.ERR-SESS-NOT-FOUND',
    SESSION_EXPIRED: 'CHAT.ERR-SESS-EXPIRED',
    SUBSCRIPTION_NOT_FOUND: 'CHAT.ERR-SUBS-NOT-FOUND',
    SUBSCRIPTION_INACTIVE: 'CHAT.ERR-SUBS-INACTIVE',
    SUBSCRIPTION_EXPIRED: 'CHAT.ERR-SUBS-EXPIRED',
    SUBSCRIPTION_LIMIT_FOR_USER: 'CHAT.ERR-SUBS-NO-USERS',
    SUBSCRIPTION_LIMIT_FOR_TOKENS: 'CHAT.ERR-SUBS-NO-TOKENS',
    REQUEST_TIMEOUT: 'CHAT.ERR-TIMEOUT',
    UNKNOWN_ERROR: 'CHAT.ERR-UNKNOWN',
    SERVICE_MAINTENANCE: 'CHAT.ERR-SERVICE-MAINTENANCE',
    MODEL_NOT_FOUND: 'CHAT.ERR-MODEL-NOT-FOUND',
    INSUFFICIENT_TOKENS: 'CHAT.ERR-INSUFFICIENT-TOKENS',
    PAYLOAD_TOO_LARGE: 'CHAT.ERR-PAYLOAD-TOO-LARGE',
} as const;

const CHAT_COPILOT_ERROR_NAME = {
    GUIDELINES: 'CHAT.ERR-MSG1',
    INAPPROPRIATE_TOPICS: 'CHAT.ERR-MSG2',
    HIGH_TRAFFIC: 'CHAT.ERR-MSG3'
} as const;

export type IChatErrorName = (typeof CHAT_ERROR_NAME)[ keyof typeof CHAT_ERROR_NAME ];

export interface IChatError
{
    text: string;
    name: IChatErrorName;
    traceID: IGuid;
    timestamp: number;
    canRetry: boolean;
    detail?: string;
}

interface IChatErrorOptions
{
    canRetry?: boolean;
}

@Injectable({
    providedIn: 'root'
})
export class ChatErrorService
{
    private static readonly DETAIL_MAX_LENGTH = 500;

    private chatError = [
        CHAT_ERROR_NAME.SESSION_NOT_FOUND,
        CHAT_ERROR_NAME.SUBSCRIPTION_NOT_FOUND,
        CHAT_ERROR_NAME.SUBSCRIPTION_INACTIVE,
        CHAT_ERROR_NAME.SUBSCRIPTION_EXPIRED,
        CHAT_ERROR_NAME.SUBSCRIPTION_LIMIT_FOR_USER,
        CHAT_ERROR_NAME.SUBSCRIPTION_LIMIT_FOR_TOKENS,
        CHAT_ERROR_NAME.REQUEST_TIMEOUT,
        CHAT_ERROR_NAME.UNKNOWN_ERROR,
        CHAT_ERROR_NAME.SERVICE_MAINTENANCE,
        CHAT_ERROR_NAME.SESSION_EXPIRED,
        CHAT_ERROR_NAME.MODEL_NOT_FOUND,
        CHAT_ERROR_NAME.INSUFFICIENT_TOKENS,
        CHAT_ERROR_NAME.PAYLOAD_TOO_LARGE,
    ];

    private chatErrorCopilot = [
        CHAT_COPILOT_ERROR_NAME.GUIDELINES,
        CHAT_COPILOT_ERROR_NAME.INAPPROPRIATE_TOPICS,
        CHAT_COPILOT_ERROR_NAME.HIGH_TRAFFIC,
    ];

    public error: BehaviorSubject<IChatError | null> = new BehaviorSubject<IChatError | null>(null);

    constructor(private translate: TranslateService) { }

    public searchError(error: IChatMessageResult, options?: IChatErrorOptions): void;
    public searchError(error: string, options?: IChatErrorOptions): void;
    public searchError(error: IChatMessageResult | string, options: IChatErrorOptions = {}): void
    {
        if (typeof error === 'string')
        {
            this.error.next(this.buildError(error, '' as IGuid, Date.now(), options));
            return;
        }

        this.error.next(this.buildError(error.text, error.traceID, error.timestamp, options));
    }

    private buildError(text: string, traceID: IGuid, timestamp: number, options: IChatErrorOptions): IChatError
    {
        const errorName = this.chatError.find(item => (item === text));
        if (errorName)
        {
            return {
                text: errorName,
                name: errorName,
                traceID,
                timestamp,
                canRetry: this.resolveCanRetry(errorName, options),
            };
        }

        const technical = this.isTechnicalError(text);

        return {
            text: technical ? CHAT_ERROR_NAME.UNKNOWN_ERROR : text,
            name: CHAT_ERROR_NAME.UNKNOWN_ERROR,
            traceID,
            timestamp,
            canRetry: this.resolveCanRetry(CHAT_ERROR_NAME.UNKNOWN_ERROR, options),
            ...(technical && text ? { detail: text.slice(0, ChatErrorService.DETAIL_MAX_LENGTH) } : {}),
        };
    }

    private resolveCanRetry(errorName: IChatErrorName, options: IChatErrorOptions): boolean
    {
        if (typeof options.canRetry === 'boolean')
        {
            return options.canRetry;
        }

        if (errorName === CHAT_ERROR_NAME.PAYLOAD_TOO_LARGE)
        {
            return false;
        }

        // return errorName === CHAT_ERROR_NAME.UNKNOWN_ERROR
        //     || errorName === CHAT_ERROR_NAME.REQUEST_TIMEOUT;

        return true;
    }

    private isTechnicalError(text: string): boolean
    {
        if (!text) { return true; }
        return /^Http failure|^HTTP \d|^Status code|XMLHttpRequest|NetworkError|ERR_|TypeError|^\s*0\s+Unknown/i.test(text);
    }

    public clearChatError(): void
    {
        this.error.next(null);
    }

    public searchChatError(error: string): string | null
    {
        const errorName = this.chatErrorCopilot.find(item => (item === error));
        if (errorName)
        {
            return this.translate.instant(errorName);
        }

        return null;
    }
}
